import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Spinner,
  Form,
} from "react-bootstrap";
import {
  FaCalendar,
  FaClock,
  FaUser,
  FaVideo,
  FaSearch,
  FaTimes,
  FaArrowRight,
  FaPlayCircle,
  FaChalkboardTeacher,
  FaCalendarAlt,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import PublicNavbar from "../../components/public/Navbar";
import Footer from "../../components/public/Footer";
import WebinarService from "../../services/webinarService";
import "../../styles/webinars.css";
import NewsletterSubscribe from "../../components/common/NewsletterSubscribe";

const Webinars = () => {
  const [webinars, setWebinars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    loadWebinars();
    window.scrollTo(0, 0);
  }, []);

  const loadWebinars = async () => {
    try {
      setLoading(true);
      const response = await WebinarService.getAllWebinars();
      setWebinars(response.data || []);
    } catch (error) {
      console.error("Failed to load webinars:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status, webinarDate, webinarTime) => {
    const now = new Date();
    const webinarDateTime = new Date(`${webinarDate}T${webinarTime}`);

    if (status === "live") {
      return { variant: "danger", text: "🔴 LIVE NOW" };
    } else if (status === "completed" || webinarDateTime < now) {
      return { variant: "secondary", text: "📹 Recording Available" };
    } else {
      return { variant: "success", text: "📅 Upcoming" };
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  const formatTime = (time) => {
    return new Date(`2000-01-01T${time}`).toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const filteredWebinars = webinars.filter((webinar) => {
    if (filter !== "all" && webinar.status !== filter) return false;
    if (
      searchTerm &&
      !webinar.title.toLowerCase().includes(searchTerm.toLowerCase()) &&
      !webinar.instructor_name.toLowerCase().includes(searchTerm.toLowerCase())
    )
      return false;
    return true;
  });

  const clearFilters = () => {
    setFilter("all");
    setSearchTerm("");
  };

  if (loading) {
    return (
      <div className="webinars-page-wrapper">
        <PublicNavbar />
        <div className="webinars-page-nav-spacer"></div>
        <div className="webinars-page-loading">
          <div className="webinars-page-loading-spinner">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Loading amazing webinars...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="webinars-page-wrapper">
      <PublicNavbar />
      <div className="webinars-page-nav-spacer"></div>

      {/* Hero Section */}
      <section className="webinars-page-hero">
        <div className="webinars-page-hero-bubbles">
          <div className="webinars-page-bubble"></div>
          <div className="webinars-page-bubble"></div>
          <div className="webinars-page-bubble"></div>
          <div className="webinars-page-bubble"></div>
          <div className="webinars-page-bubble"></div>
        </div>
        <Container>
          <div className="webinars-page-hero-content text-center">
            <div className="webinars-page-hero-badge">
              <span>🎓</span>
              <span>Free Learning Resources</span>
              <span>✨</span>
            </div>
            <h1 className="webinars-page-hero-title">
              Live & Recorded{" "}
              <span className="webinars-page-highlight">Webinars</span>
            </h1>
            <p className="webinars-page-hero-subtitle">
              Learn from industry experts, boost your career, and stay ahead
              with our free educational webinars
            </p>

            {/* Search Bar */}
            <div className="webinars-page-search-wrapper">
              <div className="webinars-page-search-inner">
                <FaSearch className="webinars-page-search-icon" />
                <Form.Control
                  type="text"
                  placeholder="Search by webinar title or instructor..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="webinars-page-search-input"
                />
                {searchTerm && (
                  <Button
                    variant="link"
                    onClick={() => setSearchTerm("")}
                    className="webinars-page-search-clear"
                  >
                    <FaTimes />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </Container>
        <div className="webinars-page-hero-wave">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1440 120"
            preserveAspectRatio="none"
          >
            <path
              fill="#ffffff"
              fillOpacity="1"
              d="M0,64L48,80C96,96,192,128,288,128C384,128,480,96,576,85.3C672,75,768,85,864,96C960,107,1056,117,1152,112C1248,107,1344,85,1392,74.7L1440,64L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z"
            ></path>
          </svg>
        </div>
      </section>

      <Container className="webinars-page-container py-5">
        {/* Stats Section */}
        <div className="webinars-page-stats">
          <Row className="g-4">
            <Col md={3} sm={6}>
              <div className="webinars-page-stat-card">
                <FaVideo className="webinars-page-stat-icon" />
                <h3>{webinars.length}</h3>
                <p>Total Webinars</p>
              </div>
            </Col>
            <Col md={3} sm={6}>
              <div className="webinars-page-stat-card">
                <FaCalendarAlt className="webinars-page-stat-icon" />
                <h3>
                  {webinars.filter((w) => w.status === "upcoming").length}
                </h3>
                <p>Upcoming Events</p>
              </div>
            </Col>
            <Col md={3} sm={6}>
              <div className="webinars-page-stat-card">
                <FaPlayCircle className="webinars-page-stat-icon" />
                <h3>
                  {webinars.filter((w) => w.status === "completed").length}
                </h3>
                <p>Recordings Available</p>
              </div>
            </Col>
            <Col md={3} sm={6}>
              <div className="webinars-page-stat-card">
                <FaChalkboardTeacher className="webinars-page-stat-icon" />
                <h3>{new Set(webinars.map((w) => w.instructor_name)).size}</h3>
                <p>Expert Instructors</p>
              </div>
            </Col>
          </Row>
        </div>

        {/* Filter Bar */}
        <div className="webinars-page-filter-bar">
          <div className="webinars-page-filter-buttons">
            <button
              className={`webinars-page-filter-btn ${filter === "all" ? "active" : ""}`}
              onClick={() => setFilter("all")}
            >
              All Webinars
            </button>
            <button
              className={`webinars-page-filter-btn ${filter === "upcoming" ? "active" : ""}`}
              onClick={() => setFilter("upcoming")}
            >
              📅 Upcoming
            </button>
            <button
              className={`webinars-page-filter-btn ${filter === "completed" ? "active" : ""}`}
              onClick={() => setFilter("completed")}
            >
              📹 Recordings
            </button>
          </div>
          {(filter !== "all" || searchTerm) && (
            <button
              className="webinars-page-clear-filters"
              onClick={clearFilters}
            >
              <FaTimes className="me-1" /> Clear Filters
            </button>
          )}
        </div>

        {/* Webinars Grid */}
        {filteredWebinars.length === 0 ? (
          <div className="webinars-page-empty-state">
            <FaVideo size={60} className="text-muted mb-3 opacity-50" />
            <h4>No webinars found</h4>
            <p className="text-muted">
              Try adjusting your search or filter criteria
            </p>
            <button className="webinars-page-empty-btn" onClick={clearFilters}>
              View All Webinars
            </button>
          </div>
        ) : (
          <Row className="g-4">
            {filteredWebinars.map((webinar) => {
              const status = getStatusBadge(
                webinar.status,
                webinar.webinar_date,
                webinar.webinar_time,
              );
              const isUpcoming = webinar.status === "upcoming";
              const isCompleted = webinar.status === "completed";

              return (
                <Col md={6} lg={4} key={webinar.id}>
                  <div className="webinars-page-card">
                    <div className="webinars-page-card-image">
                      {webinar.featured_image ? (
                        <img src={webinar.featured_image} alt={webinar.title} />
                      ) : (
                        <div className="webinars-page-card-image-placeholder">
                          <FaVideo size={48} />
                        </div>
                      )}
                      <div className="webinars-page-card-badge">
                        <span
                          className={`webinars-page-badge-${status.variant}`}
                        >
                          {status.text}
                        </span>
                      </div>
                    </div>

                    <div className="webinars-page-card-body">
                      <h3 className="webinars-page-card-title">
                        <Link to={`/webinars/${webinar.slug}`}>
                          {webinar.title}
                        </Link>
                      </h3>
                      <p className="webinars-page-card-description">
                        {truncateText(webinar.short_description, 100)}
                      </p>

                      <div className="webinars-page-card-meta">
                        <div className="webinars-page-meta-item">
                          <FaCalendar className="webinars-page-meta-icon" />
                          <span>{formatDate(webinar.webinar_date)}</span>
                        </div>
                        <div className="webinars-page-meta-item">
                          <FaClock className="webinars-page-meta-icon" />
                          <span>{formatTime(webinar.webinar_time)}</span>
                        </div>
                        <div className="webinars-page-meta-item">
                          <FaUser className="webinars-page-meta-icon" />
                          <span>{webinar.instructor_name}</span>
                        </div>
                      </div>
                    </div>

                    <div className="webinars-page-card-footer">
                      <Link
                        to={`/webinars/${webinar.slug}`}
                        className="webinars-page-card-btn"
                      >
                        {isUpcoming ? (
                          <>
                            Register Now <FaArrowRight className="ms-2" />
                          </>
                        ) : isCompleted ? (
                          <>
                            Watch Recording <FaPlayCircle className="ms-2" />
                          </>
                        ) : (
                          <>
                            View Details <FaArrowRight className="ms-2" />
                          </>
                        )}
                      </Link>
                    </div>
                  </div>
                </Col>
              );
            })}
          </Row>
        )}

        {/* Newsletter Section */}
        <div className="webinars-page-newsletter">
         <NewsletterSubscribe source="blog" variant="default" />
        </div>
      </Container>

      <Footer />
    </div>
  );
};

export default Webinars;
